/* ==========================================================================
   MIRAGE — shared audition deck
   Plain <audio> playback (works from file:// as well as a server) with the
   visualisation driven by the peak envelopes baked into manifest.js, so every
   mockup animates identically and nothing depends on Web Audio permissions.
   ========================================================================== */
(function (global) {
  /* manifest is either a bare array (first round) or {items, songs, counts} */
  var data = global.MIRAGE || [];
  var items = Array.isArray(data) ? data : (data.items || []);
  var meta = Array.isArray(data) ? {} : data;
  var byNum = {};
  items.forEach(function (it) { byNum[it.n] = it; });

  /* the demo lives outside `items` — it is the whole pack, not one sound — but
     it plays through the same deck so only one thing is ever audible */
  var demo = meta.demo || null;
  if (demo) { demo.n = 9001; demo.isDemo = true; byNum[demo.n] = demo; }

  var audio = null;
  var cur = null;          // currently loaded item
  var playing = false;
  var queue = [];          // numbers to walk through on natural end
  var vol = 0.85;
  var subs = { change: [], tick: [] };
  var level = 0;           // smoothed 0..1 envelope at the playhead
  var raf = 0;

  function emit(name, arg) {
    subs[name].forEach(function (fn) { fn(arg); });
  }

  function ensure() {
    if (audio) return audio;
    audio = new Audio();
    audio.preload = 'none';
    audio.volume = vol;
    audio.addEventListener('ended', function () {
      var i = queue.indexOf(cur && cur.n);
      if (i > -1 && i < queue.length - 1) { play(queue[i + 1]); return; }
      stop();
    });
    audio.addEventListener('play', function () { playing = true; loop(); emit('change', cur); });
    audio.addEventListener('pause', function () { playing = false; emit('change', playing ? cur : null); });
    return audio;
  }

  /* envelope value at the current playhead, linearly interpolated then smoothed */
  function envelope() {
    if (!cur || !playing || !audio.duration) return 0;
    var p = audio.currentTime / audio.duration;
    var pk = cur.peaks || [0];
    var x = Math.min(pk.length - 1, Math.max(0, p * (pk.length - 1)));
    var i = Math.floor(x), f = x - i;
    var a = pk[i] || 0, b = pk[Math.min(pk.length - 1, i + 1)] || 0;
    return a + (b - a) * f;
  }

  function loop() {
    cancelAnimationFrame(raf);
    var target = envelope();
    level += (target - level) * (target > level ? 0.45 : 0.12);
    emit('tick', {
      item: cur,
      playing: playing,
      level: level,
      progress: cur && audio.duration ? audio.currentTime / audio.duration : 0,
      time: audio ? audio.currentTime : 0,
      duration: cur ? cur.dur : 0
    });
    if (playing || level > 0.002) raf = requestAnimationFrame(loop);
  }

  /* On Shopify the audio lives in the theme's assets/ folder, which has a
     different base URL. The page sets MIRAGE_BASE; locally it stays unset and
     the manifest's own relative path is used. */
  function srcOf(it) {
    return global.MIRAGE_BASE ? global.MIRAGE_BASE + it.url.split('/').pop() : it.url;
  }

  function play(n) {
    var it = byNum[n];
    if (!it) return;
    ensure();
    if (cur && cur.n === n && playing) { pause(); return; }
    cur = it;
    audio.src = srcOf(it);
    audio.currentTime = 0;
    var p = audio.play();
    if (p && p.catch) p.catch(function () { /* autoplay blocked — needs a tap */ });
    emit('change', cur);
  }

  function pause() { if (audio) audio.pause(); }

  function stop() {
    if (audio) { audio.pause(); audio.currentTime = 0; }
    playing = false;
    cur = null;
    emit('change', null);
    loop();
  }

  function seek(p) {
    if (audio && audio.duration) audio.currentTime = Math.max(0, Math.min(1, p)) * audio.duration;
  }

  global.Deck = {
    items: items,
    demo: demo,
    songs: meta.songs || [],
    counts: meta.counts || {},
    get: function (n) { return byNum[n]; },
    play: play,
    toggle: play,
    pause: pause,
    stop: stop,
    seek: seek,
    setQueue: function (arr) { queue = arr.slice(); },
    setVolume: function (v) { vol = v; if (audio) audio.volume = v; },
    get current() { return playing ? cur : null; },
    get playing() { return playing; },
    on: function (name, fn) { if (subs[name]) subs[name].push(fn); return this; }
  };

  /* ---------- helpers every mockup uses ---------- */

  /* time in seconds -> 0:00 */
  global.Deck.clock = function (s) {
    s = Math.max(0, s || 0);
    return Math.floor(s / 60) + ':' + String(Math.floor(s % 60)).padStart(2, '0');
  };

  /* draw a peak array into a canvas as a mirrored bar waveform */
  global.Deck.drawWave = function (cv, peaks, opts) {
    opts = opts || {};
    var dpr = Math.min(2, global.devicePixelRatio || 1);
    var w = cv.clientWidth, h = cv.clientHeight;
    if (!w || !h) return;
    if (cv.width !== w * dpr) { cv.width = w * dpr; cv.height = h * dpr; }
    var c = cv.getContext('2d');
    c.setTransform(dpr, 0, 0, dpr, 0, 0);
    c.clearRect(0, 0, w, h);
    var n = peaks.length;
    var gap = opts.gap == null ? 1 : opts.gap;
    var bw = Math.max(1, w / n - gap);
    var prog = opts.progress == null ? 1 : opts.progress;
    for (var i = 0; i < n; i++) {
      var x = i * (w / n);
      var v = Math.max(0.03, peaks[i]);
      var bh = v * h * (opts.scale || 0.92);
      c.fillStyle = (i / n) <= prog ? (opts.on || '#fff') : (opts.off || 'rgba(255,255,255,.22)');
      if (opts.round) {
        var r = Math.min(bw / 2, 2);
        c.beginPath();
        if (c.roundRect) c.roundRect(x, (h - bh) / 2, bw, bh, r);
        else c.rect(x, (h - bh) / 2, bw, bh);
        c.fill();
      } else {
        c.fillRect(x, (h - bh) / 2, bw, bh);
      }
    }
  };
})(window);
